<script setup lang="ts">
import { ensureTabVisible } from '@/components/BlockContentTab.vue';
import { type BlockMenuItem, headingLevels } from './BlockToc.vue';
import vueScrollTo from 'vue-scrollto';
import MaterialIcon from '@/components/MaterialIcon.vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineProps<{
  scrollto: string | null;
  title: string;
  items?: BlockMenuItem[];
  level?: (typeof headingLevels)[number];
}>();

const showSubtitle = ref(false);

const toggleSubtitle = () => {
  showSubtitle.value = !showSubtitle.value;
};
</script>

<template>
  <li>
    <a
      v-if="scrollto"
      class="my-2 block p-1 px-2 hover:cursor-pointer hover:underline"
      :class="[
        { 'text-left-menu-1': level === 'h2' },
        { 'text-left-menu-2': level === 'h3' },
      ]"
      href="#"
      @click.prevent="
        ensureTabVisible(scrollto);
        vueScrollTo.scrollTo(`#${scrollto}`);
      "
    >
      <div class="flex items-center">
        <span
          class="mr-2 text-2xl hover:text-gray-500"
          :class="{
            'cursor-pointer': level === 'h2' && items && items.length > 0,
          }"
          :aria-label="showSubtitle ? t('block.collapse') : t('block.expand')"
          @click.stop="toggleSubtitle"
        >
          <MaterialIcon v-if="level === 'h2' && items && items.length > 0">
            {{ showSubtitle ? 'expand_less' : 'expand_more' }}
          </MaterialIcon>
          <MaterialIcon v-else class="invisible">expand_more</MaterialIcon>
        </span>
        <span class="ml-2">{{ title }}</span>
      </div>
    </a>
    <template v-else>
      <div class="my-2 block items-center p-1 px-2">
        <span
          class="mr-2 text-2xl hover:text-gray-500"
          :class="{
            'cursor-pointer': level === 'h2' && items && items.length > 0,
          }"
          @click.stop="toggleSubtitle"
        >
          <MaterialIcon
            v-if="level === 'h2' && items && items.length > 0"
            :aria-label="showSubtitle ? t('block.collapse') : t('block.expand')"
          >
            {{ showSubtitle ? 'expand_less' : 'expand_more' }}
          </MaterialIcon>
          <MaterialIcon v-else class="invisible">expand_more</MaterialIcon>
        </span>
        <span class="ml-2">{{ title }}</span>
      </div>
    </template>
    <ul v-if="items && showSubtitle" class="pl-6">
      <BlockTocItem
        v-for="(item, index) in items"
        :key="index"
        :scrollto="item.id"
        :title="item.title"
        :items="item.items"
        :level="headingLevels[headingLevels.indexOf(level ?? 'h2') + 1]"
      />
    </ul>
  </li>
</template>
